import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/Tabs';
import { Paperclip, Send, CheckCircle2, X, UserCircle, ShieldAlert, Users, ChevronsUpDown, AlertTriangle, Zap, Brain, Info, Settings, ArrowRightLeft } from 'lucide-react';
import { format, parseISO, addMonths, addYears, subDays, subMonths as dateFnsSubMonths, subYears, subHours, subMinutes, addDays } from 'date-fns';
import { useToast } from '../hooks/use-toast';

// Mock data as requested to be in the same file
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const generateMonthlyInvoices = () => {
  return months.map(month => ({
    month,
    count: Math.floor(Math.random() * 30) + 5,
  }));
};

const generateOverallDocumentStats = (monthlyInvoices, isFreeUserWithHighUsage = false) => {
  const totalInvoices = monthlyInvoices.reduce((sum, inv) => sum + inv.count, 0);
  return [
    { documentType: 'Invoices', totalCount: isFreeUserWithHighUsage ? Math.floor(Math.random() * 10) + 11 : totalInvoices },
    { documentType: 'Order Forms', totalCount: isFreeUserWithHighUsage ? Math.floor(Math.random() * 10) + 11 : Math.floor(Math.random() * 50) + 10 },
    { documentType: 'Proposals', totalCount: Math.floor(Math.random() * 40) + 5 },
    { documentType: 'Contracts', totalCount: Math.floor(Math.random() * 60) },
  ];
};

const sampleActiveBlades = () => {
  const allBlades = ['Invoicing', 'E-Signatures', 'Project Management', 'Client Portal', 'Time Tracking'];
  const activeCount = Math.floor(Math.random() * 3) + 2;
  return allBlades.sort(() => 0.5 - Math.random()).slice(0, activeCount);
};

const generateNextRenewalDate = (renewalType) => {
  const today = new Date();
  if (renewalType === 'Monthly') {
    return format(addMonths(today, Math.floor(Math.random() * 3) + 1), 'yyyy-MM-dd');
  } else {
    return format(addYears(today, 1), 'yyyy-MM-dd');
  }
};

const today = new Date();

const usersInitialData = [
  {
    id: '1',
    name: 'Alice Wonderland',
    email: 'alice@wonderland.com',
    licenseType: 'Premium',
    status: 'Active',
    avatar: 'https://placehold.co/80x80.png',
    signatureDocumentsSent: Math.floor(Math.random() * 10) + 1,
    renewalType: 'Annual',
    contacts: ['cheshire.cat@wonderland.com', 'mad.hatter@wonderland.com'],
    domainName: 'wonderland.com',
    phoneNumber: '555-0101',
    seatCount: 5,
    creationDate: format(subYears(today, 2), 'yyyy-MM-dd'),
  },
  {
    id: '2',
    name: 'Bob The Builder',
    email: 'bob@buildit.com',
    licenseType: 'Free',
    status: 'Active',
    avatar: 'https://placehold.co/80x80.png',
    signatureDocumentsSent: Math.floor(Math.random() * 10),
    renewalType: 'Monthly',
    contacts: ['wendy@buildit.com'],
    domainName: 'buildit.com',
    phoneNumber: '555-0102',
    seatCount: 1,
    creationDate: format(dateFnsSubMonths(today, 7), 'yyyy-MM-dd'),
    isPotentialPaid: true,
  },
  {
    id: '3',
    name: 'Charlie Brown',
    email: 'charlie@peanuts.com',
    licenseType: 'Enterprise',
    status: 'Inactive',
    avatar: 'https://placehold.co/80x80.png',
    signatureDocumentsSent: Math.floor(Math.random() * 15) + 5,
    renewalType: 'Annual',
    contacts: ['snoopy@peanuts.com', 'lucy@peanuts.com', 'linus@peanuts.com'],
    domainName: 'peanuts.com',
    phoneNumber: '555-0103',
    seatCount: 25,
    creationDate: format(dateFnsSubMonths(today, 3), 'yyyy-MM-dd'),
  },
  {
    id: '4',
    name: 'Diana Prince',
    email: 'diana@themyscira.org',
    licenseType: 'Premium',
    status: 'Active',
    avatar: 'https://placehold.co/80x80.png',
    signatureDocumentsSent: Math.floor(Math.random() * 8) + 2,
    renewalType: 'Monthly',
    contacts: ['steve.trevor@example.com'],
    domainName: 'themyscira.org',
    phoneNumber: '555-0104',
    seatCount: 3,
    creationDate: format(subDays(dateFnsSubMonths(today, 1), 15), 'yyyy-MM-dd'),
  },
  {
    id: '5',
    name: 'Edward Scissorhands',
    email: 'edward@gentlefolks.net',
    licenseType: 'Free',
    status: 'Inactive',
    avatar: 'https://placehold.co/80x80.png',
    signatureDocumentsSent: Math.floor(Math.random() * 5),
    renewalType: 'Annual',
    contacts: [],
    domainName: 'gentlefolks.net',
    phoneNumber: '555-0105',
    seatCount: 1,
    creationDate: format(subDays(today, 10), 'yyyy-MM-dd'),
  },
  {
    id: '6',
    name: 'Fiona Gallagher',
    email: 'fiona@southside.com',
    licenseType: 'Free',
    status: 'Active',
    avatar: 'https://placehold.co/80x80.png',
    signatureDocumentsSent: 2,
    renewalType: 'Monthly',
    contacts: ['lip@southside.com'],
    domainName: 'southside.com',
    phoneNumber: '555-0106',
    seatCount: 1,
    creationDate: format(subDays(today, 5), 'yyyy-MM-dd'),
    isPotentialPaid: true,
  },
  {
    id: '7',
    name: 'George Jetson',
    email: 'george@spacely.com',
    licenseType: 'Enterprise',
    status: 'Active',
    avatar: 'https://placehold.co/80x80.png',
    signatureDocumentsSent: 20,
    renewalType: 'Annual',
    contacts: ['jane@spacely.com', 'astro@spacely.com'],
    domainName: 'spacely.com',
    phoneNumber: '555-0107',
    seatCount: 50,
    creationDate: format(dateFnsSubMonths(today, 14), 'yyyy-MM-dd'),
  },
];

export const mockUsers = usersInitialData.map(user => {
  const monthlyInvoices = generateMonthlyInvoices();
  const overallDocumentStats = generateOverallDocumentStats(monthlyInvoices, user.isPotentialPaid);
  const activeBlades = sampleActiveBlades();
  const nextRenewalDate = generateNextRenewalDate(user.renewalType);
  
  const { isPotentialPaid, ...restOfUser } = user;

  return {
    ...restOfUser,
    monthlyInvoices,
    overallDocumentStats,
    activeBlades,
    nextRenewalDate,
  };
});


export const mockRevenueData = [
  { year: 2022, totalSales: 120000, mrr: 10000, arr: 120000 },
  { year: 2023, totalSales: 180000, mrr: 15000, arr: 180000 },
  { year: 2024, totalSales: 250000, mrr: 20000, arr: 240000 },
];

export const yearlyRevenueDetails = {
  2022: {
    sales: Array.from({ length: 12 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 5000) + 8000 })),
    mrr: Array.from({ length: 12 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 500) + 9500 })),
    arr: Array.from({ length: 12 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 1000) + 115000 })),
  },
  2023: {
    sales: Array.from({ length: 12 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 6000) + 12000 })),
    mrr: Array.from({ length: 12 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 600) + 14500 })),
    arr: Array.from({ length: 12 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 1200) + 175000 })),
  },
  2024: {
    sales: Array.from({ length: new Date().getMonth() + 1 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 7000) + 18000 })),
    mrr: Array.from({ length: new Date().getMonth() + 1 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 700) + 19500 })),
    arr: Array.from({ length: new Date().getMonth() + 1 }, (_, i) => ({ month: new Date(0, i).toLocaleString('default', { month: 'short' }), amount: Math.floor(Math.random() * 1500) + 235000 })),
  }
};

const generateInitialResponse = (userName) => {
  return [
    {
      id: `resp-${Math.random().toString(36).substring(7)}`,
      responseText: `Hi ${userName.split(' ')[0]},\n\nWe have received your request and will update you in 24 business hours.`,
      respondedBy: 'System',
      respondedAt: subMinutes(new Date(), 5).toISOString(),
      imageUrls: [],
    }
  ];
};

export const mockTeamMembers = ['Alice Admin', 'Bob Support', 'Charlie Tech', 'Unassigned'];

export const mockTickets = [
  {
    id: 'TICKET001',
    subject: 'Login Issue on Mobile App',
    description: 'I am unable to log in to the mobile application. It keeps saying "Invalid Credentials" even though I am sure my password is correct. I tried resetting, but the email never arrived.',
    status: 'Pending',
    raisedByName: 'Alice Wonderland',
    raisedByEmail: 'alice@wonderland.com',
    createdAt: subDays(today, 2).toISOString(),
    updatedAt: subHours(today, 3).toISOString(),
    responses: [
      ...generateInitialResponse('Alice Wonderland'),
      {
        id: 'RESP001A',
        responseText: 'Hi Alice, I have checked your account and manually resent the password reset email. Please check your spam folder as well. Let me know if you receive it.',
        respondedBy: 'Admin Support',
        respondedAt: subHours(today, 2).toISOString(),
        imageUrls: [],
      }
    ],
    assignedTo: 'Alice Admin',
    priority: 'High',
    urgency: 'High',
    preferredResponseType: 'Simple',
    ticketType: 'Support',
  },
  {
    id: 'TICKET002',
    subject: 'Invoice Template Customization Query',
    description: 'How can I add my company logo to the top right of the invoice template? I found the setting for the logo but it appears on the left.',
    status: 'Pending',
    raisedByName: 'Bob The Builder',
    raisedByEmail: 'bob@buildit.com',
    createdAt: subDays(today, 1).toISOString(),
    updatedAt: subHours(today, 1).toISOString(),
    responses: generateInitialResponse('Bob The Builder'),
    assignedTo: 'Bob Support',
    priority: 'Medium',
    urgency: 'Medium',
    preferredResponseType: 'Technical',
    ticketType: 'Support',
  },
  {
    id: 'TICKET003',
    subject: 'API Integration Error - 403 Forbidden',
    description: 'When trying to connect to your API using the provided key, I am consistently getting a 403 Forbidden error. My IP address is whitelisted. Endpoint: /v1/users',
    status: 'Resolved',
    raisedByName: 'Charlie Brown',
    raisedByEmail: 'charlie@peanuts.com',
    createdAt: subDays(today, 5).toISOString(),
    updatedAt: subDays(today, 3).toISOString(),
    responses: [
      ...generateInitialResponse('Charlie Brown'),
      {
        id: 'RESP003A',
        responseText: 'Hi Charlie, we identified a temporary issue with API key propagation on one of our servers. This has now been resolved. Please try again.',
        respondedBy: 'Admin Support',
        respondedAt: subDays(today, 4).toISOString(),
      },
      {
        id: 'RESP003B',
        responseText: 'Thanks, it works now!',
        respondedBy: 'Charlie Brown (via email mock)',
        respondedAt: subDays(today, 3).toISOString(),
      },
      {
        id: 'RESP003C',
        responseText: 'Great to hear! Closing this ticket.',
        respondedBy: 'Admin Support',
        respondedAt: subDays(today, 3).toISOString(),
      }
    ],
    assignedTo: 'Charlie Tech',
    priority: 'High',
    urgency: 'High',
    preferredResponseType: 'Technical',
    ticketType: 'Support',
  },
  {
    id: 'TICKET004',
    subject: 'Feature Request: Dark Mode for Dashboard',
    description: 'It would be great if the admin dashboard had a dark mode option. It would be easier on the eyes for prolonged use.',
    status: 'Pending',
    raisedByName: 'Diana Prince',
    raisedByEmail: 'diana@themyscira.org',
    createdAt: subHours(today, 5).toISOString(),
    updatedAt: subHours(today, 5).toISOString(),
    responses: generateInitialResponse('Diana Prince'),
    priority: 'Low',
    urgency: 'Low',
    preferredResponseType: 'Simple',
    ticketType: 'Enhancement',
  },
];

export const ticketPriorities = ['Low', 'Medium', 'High'];

export default function TicketDetailModal({ ticket, isOpen, onClose, onTicketUpdate , onAddResponse }) {
  const [newResponseText, setNewResponseText] = useState('');
  const [newImageUrls, setNewImageUrls] = useState('');
  const [assignedTo, setAssignedTo] = useState(ticket.assignedTo || 'Unassigned');
  const [priority, setPriority] = useState(ticket.priority || 'Medium');
  const [currentTicketType, setCurrentTicketType] = useState(ticket.ticketType || 'Support');
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setAssignedTo(ticket.assignedTo || 'Unassigned');
      setPriority(ticket.priority || 'Medium');
      setCurrentTicketType(ticket.ticketType || 'Support');
      setNewResponseText('');
      setNewImageUrls('');
    }
  }, [isOpen, ticket]);

  const handleTicketTypeChange = () => {
    const newType = currentTicketType === 'Support' ? 'Enhancement' : 'Support';
    const updatedTicket = {
      ...ticket,
      ticketType: newType,
      updatedAt: new Date().toISOString(),
    };
    onTicketUpdate(updatedTicket);
    setCurrentTicketType(newType);
    toast({ title: "Ticket Type Updated", description: `Ticket marked as ${newType}.` });
  };
 const handleSendResponse = async (e, markAsResolved = false) => {
    e.preventDefault();
    if (!newResponseText.trim() && !markAsResolved) {
      toast({ title: "Response Required", description: "Please enter a response.", variant: "destructive" });
      return;
    }

    try {
      // Call the API to save the response
      if (newResponseText.trim() && onAddResponse) {
        console.log('=== MODAL CALLING onAddResponse ===');
        console.log('Ticket ID:', ticket.id);
        console.log('Response Text:', newResponseText);
        
        await onAddResponse(ticket.id, newResponseText, false); // false = not internal
      }

      // Update ticket properties (assignment, priority, status)
      const updatedTicket = {
        ...ticket,
        status: markAsResolved ? 'Resolved' : ticket.status,
        updatedAt: new Date().toISOString(),
        assignedTo: assignedTo === 'Unassigned' ? undefined : assignedTo,
        priority: priority,
        ticketType: currentTicketType,
      };

      // Only call onTicketUpdate if properties changed
      if (assignedTo !== ticket.assignedTo || priority !== ticket.priority || markAsResolved) {
        onTicketUpdate(updatedTicket);
      }

      toast({ 
        title: `Ticket Updated ${markAsResolved ? 'and Resolved' : ''}`, 
        description: "The ticket has been successfully updated." 
      });
      
      setNewResponseText('');
      setNewImageUrls('');
      
      if (markAsResolved) {
        onClose();
      }
    } catch (error) {
      console.error('Error in handleSendResponse:', error);
      toast({
        title: "Error",
        description: "Failed to send response. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleMarkAsResolved = (e) => {
    if (!newResponseText.trim() && ticket.responses.length <= 1 && ticket.status === 'Pending' && ticket.responses[0].respondedBy === 'System') {
      const confirmResolve = window.confirm("You are about to resolve this ticket without adding a new response. The last response sent will be considered the resolution. Are you sure?");
      if (!confirmResolve) {
        return;
      }
    }
    handleSendResponse(e, true);
  };

  const getPriorityBadgeClass = (prio) => {
    switch (prio) {
      case 'High': return 'bg-red-100 text-red-700 border-red-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'Low': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle className="text-2xl flex items-center">
                Ticket: <span className="ml-2 font-semibold text-primary truncate max-w-md">{ticket.subject}</span>
              </DialogTitle>
              <DialogDescription className="mt-1">
                Raised by {ticket.raisedByName} ({ticket.raisedByEmail}) on {format(parseISO(ticket.createdAt), 'MMM d, yyyy HH:mm')}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={ticket.status === 'Resolved' ? 'default' : 'secondary'}
                className={`text-sm py-1 px-2.5 ${ticket.status === 'Resolved' ? 'bg-green-600 text-white' : 'bg-accent text-accent-foreground'}`}>
                {ticket.status}
              </Badge>
              <Badge variant="outline" className={`text-sm py-1 px-2.5 ${getPriorityBadgeClass(ticket.priority)}`}>
                <AlertTriangle className="mr-1.5 h-3.5 w-3.5 " /> Admin Priority: {ticket.priority || 'N/A'}
              </Badge>
              <Badge variant={currentTicketType === 'Enhancement' ? 'default' : 'outline'} className={`text-sm py-1 px-2.5 ${currentTicketType === 'Enhancement' ? 'bg-purple-600 text-white' : ''}`}>
                {currentTicketType === 'Enhancement' ? <Zap className="mr-1.5 h-3.5 w-3.5" /> : <Info className="mr-1.5 h-3.5 w-3.5" />}
                Type: {currentTicketType}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <Separator />
        <Tabs defaultValue="respond" className="flex-grow flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="respond"><Settings className="mr-2 h-4 w-4"/>Respond & Manage</TabsTrigger>
            <TabsTrigger value="enhancement"><ArrowRightLeft className="mr-2 h-4 w-4"/>Convert Ticket Type</TabsTrigger>
          </TabsList>

          <TabsContent value="respond" className="flex-grow overflow-y-auto pr-4 -mr-4 mb-4">
            <ScrollArea className="h-full">
              <div className="space-y-6 py-2">
                <div>
                  <h3 className="font-semibold mb-1 text-lg">Initial Query:</h3>
                  <p className="text-sm text-muted-foreground bg-secondary p-3 rounded-md whitespace-pre-wrap">{ticket.description}</p>
                </div>
                
                {ticket.urgency && (
                  <div className="text-sm">
                    <span className="font-semibold">User Stated Urgency: </span>
                    <Badge variant="outline" className={getPriorityBadgeClass(ticket.urgency)}>
                      {ticket.urgency}
                    </Badge>
                  </div>
                )}
                {ticket.preferredResponseType && (
                  <div className="text-sm">
                    <span className="font-semibold">Preferred Response: </span>
                    <Badge variant="outline" className="bg-indigo-100 text-indigo-700 border-indigo-300">
                      {ticket.preferredResponseType}
                    </Badge>
                  </div>
                )}


                {ticket.assignedTo && ticket.assignedTo !== 'Unassigned' && (
                  <div className="text-sm">
                    <span className="font-semibold">Assigned to: </span>
                    <span className="text-primary">{ticket.assignedTo}</span>
                  </div>
                )}

                {ticket.responses.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 text-lg">Response History:</h3>
                    <div className="space-y-4">
                      {ticket.responses.map((response) => (
                        <div key={response.id} className={`p-3 rounded-md shadow-sm border ${response.respondedBy === 'System' ? 'bg-blue-50 border-blue-200' : response.respondedBy.toLowerCase().includes('admin') ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs font-semibold flex items-center">
                              {response.respondedBy === 'System' ? <ShieldAlert className="w-4 h-4 mr-1.5 text-blue-600" /> : response.respondedBy.toLowerCase().includes('admin') ? <UserCircle className="w-4 h-4 mr-1.5 text-green-700" /> : <UserCircle className="w-4 h-4 mr-1.5 text-gray-600" />}
                              {response.respondedBy}
                            </p>
                            <p className="text-xs text-muted-foreground">{format(parseISO(response.respondedAt), 'MMM d, yyyy HH:mm')}</p>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{response.responseText}</p>
                          {response.imageUrls && response.imageUrls.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium">Attached Images:</p>
                              <ul className="list-disc list-inside pl-1">
                                {response.imageUrls.map((url, idx) => (
                                  <li key={idx} className="text-xs">
                                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate block max-w-full">
                                      {url}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            
            {ticket.status === 'Pending' && (
              <form onSubmit={(e) => handleSendResponse(e, false)} className="mt-auto pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="assignTo" className="font-semibold flex items-center mb-1">
                      <Users className="mr-1.5 h-4 w-4" /> Assign to:
                    </Label>
                    <Select value={assignedTo} onValueChange={setAssignedTo}>
                      <SelectTrigger id="assignTo">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        {mockTeamMembers.map(member => (
                          <SelectItem key={member} value={member}>{member}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="priority" className="font-semibold flex items-center mb-1">
                      <ChevronsUpDown className="mr-1.5 h-4 w-4" /> Set Admin Priority:
                    </Label>
                    <Select value={priority} onValueChange={(val) => setPriority(val)}>
                      <SelectTrigger id="priority">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {ticketPriorities.map(prio => (
                          <SelectItem key={prio} value={prio}>{prio}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="newResponse" className="font-semibold">Your Response:</Label>
                    <Textarea
                      id="newResponse"
                      value={newResponseText}
                      onChange={(e) => setNewResponseText(e.target.value)}
                      placeholder="Type your response here..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="imageUrls" className="flex items-center">
                      <Paperclip className="mr-1.5 h-4 w-4" /> Attach Image URLs (comma-separated):
                    </Label>
                    <Input
                      id="imageUrls"
                      type="text"
                      value={newImageUrls}
                      onChange={(e) => setNewImageUrls(e.target.value)}
                      placeholder="e.g., https://placehold.co/image1.png, https://placehold.co/image2.png"
                      className="mt-1"
                    />
                  </div>
                </div>
                <DialogFooter className="mt-6 gap-2 sm:gap-0">
                  <Button type="button" variant="outline" onClick={onClose}>
                    <X className="mr-2 h-4 w-4" /> Cancel
                  </Button>
                  <Button type="submit" variant="default">
                    <Send className="mr-2 h-4 w-4" /> Send Response
                  </Button>
                    <Button type="button" variant="destructive" onClick={handleMarkAsResolved} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle2 className="mr-2 h-4 w-4" /> Mark as Resolved
                  </Button>
                </DialogFooter>
              </form>
            )}
            {ticket.status === 'Resolved' && (
                <div className="mt-auto pt-4 border-t text-center">
                    <p className="text-green-600 font-semibold flex items-center justify-center">
                        <CheckCircle2 className="mr-2 h-5 w-5"/> This ticket has been resolved.
                    </p>
                </div>
            )}
          </TabsContent>

          <TabsContent value="enhancement" className="flex-grow overflow-y-auto p-4">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Zap className="mr-2 h-5 w-5 text-purple-600" />
                Convert Ticket to Enhancement Request
              </h3>
              <p className="text-sm text-muted-foreground">
                If this ticket represents a feature request or an improvement idea, you can mark it as an enhancement.
                This helps in tracking and prioritizing product development.
              </p>
              <Separator />
              <div className="flex items-center justify-between p-4 bg-secondary rounded-md">
                  <div>
                    <p className="font-medium">Current Ticket Type:</p>
                    <Badge variant={currentTicketType === 'Enhancement' ? 'default' : 'secondary'} 
                          className={currentTicketType === 'Enhancement' ? 'bg-purple-600 text-white mt-1' : 'mt-1'}>
                      {currentTicketType}
                    </Badge>
                  </div>
                  <Button onClick={handleTicketTypeChange} variant={currentTicketType === 'Enhancement' ? "outline" : "default"}>
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    {currentTicketType === 'Support' ? 'Mark as Enhancement' : 'Revert to Support Ticket'}
                  </Button>
              </div>
                {currentTicketType === 'Enhancement' && (
                <div className="p-4 border border-purple-200 bg-purple-50 rounded-md">
                  <p className="text-sm text-purple-700 flex items-center">
                    <Brain className="mr-2 h-4 w-4" />This ticket is now tracked as an enhancement request.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter className="mt-auto pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}